/**
 * KAN-858: ручная переписка в карточке клиента приложения менеджера:
 * подписи, размер страницы ленты, частота обновления и строки блока
 * "Что сделать" (причина внимания, задача, пометка следующего контакта).
 */
import type { AttentionItem, AttentionTask } from '@/services/api/dashboard';
import type { Client } from '@/types/client';

/** Сколько сообщений лента грузит сначала и добавляет при прокрутке вверх. */
export const MOBILE_FEED_PAGE = 30;
/** Предел backend для ?limit= переписки клиента. */
export const MOBILE_FEED_MAX = 2000;
/** Лента перечитывается так часто, пока страница видна (ответы клиента). */
export const MOBILE_FEED_POLL_MS = 10_000;

export const CHAT_LEAD_BOT = 'Ведёт: бот';
export const CHAT_LEAD_MANAGER = 'Ведёт: менеджер (ручной режим)';
export const TAKE_OVER_LABEL = 'Взять переписку';
export const RETURN_TO_BOT_LABEL = 'Вернуть боту';
export const RETURN_CONFIRM_TEXT =
  'Вернуть клиента боту? Ручной режим выключится, бот снова будет отвечать и писать напоминания.';
export const TAKE_OVER_CONFIRM_TEXT = 'Бот замолчит, пока вы ведёте переписку';
export const TAKE_AND_SEND_LABEL = 'Взять и отправить';
export const READ_ONLY_HINT = 'Клиент у другого менеджера: переписку можно только читать';
export const LOAD_EARLIER_LABEL = 'Показать ранние сообщения';
export const REPLY_LABEL = 'Ответить';

/** id блока поля ввода переписки (для "Ответить"). */
export const MOBILE_COMPOSER_ID = 'mobile-chat-composer';

/** "Ответить": к полю ввода и фокус в нём. */
export function focusMobileComposer(): void {
  const box = document.getElementById(MOBILE_COMPOSER_ID);
  box?.scrollIntoView?.({ block: 'end', behavior: 'smooth' });
  box?.querySelector('textarea')?.focus();
}

/** Следующий предел ленты при прокрутке вверх. */
export function nextFeedLimit(limit: number): number {
  return Math.min(limit + MOBILE_FEED_PAGE, MOBILE_FEED_MAX);
}

/** Есть ли сообщения раньше загруженных: backend вернул ровно limit. */
export function feedHasMore(count: number, limit: number): boolean {
  return count >= limit && limit < MOBILE_FEED_MAX;
}

export interface ManagerTaskView {
  /** Почему клиент ждёт человека (строка внимания). */
  reason: string;
  /** Задача менеджеру из лестницы напоминаний. */
  task: AttentionTask | null;
  /** Пометка следующего контакта из CRM. */
  note: string;
}

/**
 * Что сделать менеджеру с клиентом: причина строки внимания, задача и
 * пометка CRM без повторов; null, когда сказать нечего.
 */
export function managerTaskView(
  item: Pick<AttentionItem, 'reason_text' | 'task'> | null | undefined,
  client: Pick<Client, 'followup_note'>
): ManagerTaskView | null {
  const reason = (item?.reason_text ?? '').trim();
  const task = item?.task && item.task.text.trim() ? item.task : null;
  const rawNote = (client.followup_note ?? '').trim();
  const note = rawNote && rawNote !== reason && rawNote !== task?.text.trim() ? rawNote : '';
  if (!reason && !task && !note) return null;
  return { reason, task, note };
}
